// Where an add / edit form goes after a successful Save: back to the page the admin opened it from
// (that page loads again, so it shows what was saved), or, when the form was opened directly (a typed or
// bookmarked address, a new tab), to the record's details page, or its list when it has none.
//
// NavigationTracker (in App.js) keeps the admin website's own history entries, the same as the browser's
// Back button sees them, in sessionStorage so a reload keeps them. A form calls leaveForm only once its
// save has succeeded; a failed save or a question shown before saving (shared phone, "Is this the same
// person?") never does, so the form stays.
import { useEffect } from 'react';
import { withRouter } from 'react-router-dom';

const STORE_KEY = 'navigationEntries';

// { entries: [{ key, path }], index }: the in-app history and where the admin is in it
let state = null;

function load() {
	if (state !== null) {
		return state;
	}
	try {
		const saved = JSON.parse(sessionStorage.getItem(STORE_KEY));
		if (saved && Array.isArray(saved.entries) && typeof saved.index === 'number') {
			state = saved;
			return state;
		}
	} catch (e) {
		// unreadable or blocked storage: start afresh
	}
	state = { entries: [], index: -1 };
	return state;
}

function save() {
	try {
		sessionStorage.setItem(STORE_KEY, JSON.stringify(state));
	} catch (e) {
		// kept in memory only
	}
}

function pathOf(location) {
	return location.pathname + (location.search || '') + (location.hash || '');
}

// The key of a history entry; react-router gives the first page loaded none, so its path stands in
function keyOf(location) {
	return location.key || 'path:' + pathOf(location);
}

// Exported for the tests
export function record(location, action) {
	const s = load();
	const entry = { key: keyOf(location), path: pathOf(location) };
	if (action === 'PUSH') {
		s.entries = s.entries.slice(0, s.index + 1).concat([entry]);
		s.index = s.entries.length - 1;
	} else if (action === 'REPLACE' && s.index >= 0) {
		s.entries[s.index] = entry;
	} else {
		// Back / Forward, a reload or the first page: find the entry; one not known starts the history afresh
		const found = s.entries.findIndex((e) => e.key === entry.key);
		if (found >= 0) {
			s.index = found;
		} else {
			s.entries = [entry];
			s.index = 0;
		}
	}
	save();
}

// Exported for the tests
export function reset() {
	state = { entries: [], index: -1 };
	save();
}

// Mounted once inside the Router: keeps the entries as the admin moves around
export const NavigationTracker = withRouter(function NavigationTracker({ history }) {
	useEffect(() => {
		record(history.location, 'POP');
		return history.listen((location, action) => record(location, action));
	}, [history]);
	return null;
});

// The page the admin opened the form from ({ key, path }), or null when it was opened directly. Asked at
// save time: when the form opens, the router shows it before NavigationTracker hears of the move.
export function cameFrom(history) {
	const s = load();
	const current = s.entries[s.index];
	return current && current.key === keyOf(history.location) && s.index > 0 ? s.entries[s.index - 1] : null;
}

// After a successful save: back to the page the form was opened from (it loads again, showing what was
// saved), otherwise to fallback (the record's details page or its list) in place of the form.
export function leaveForm(history, fallback) {
	if (cameFrom(history)) {
		history.goBack();
	} else {
		history.replace(fallback);
	}
}

// A page's "Back to ..." button: to path, by going back when the admin came straight from it (that page
// loads again), otherwise (opened directly, or from somewhere else) by opening it.
export function goBackTo(history, path) {
	const from = cameFrom(history);
	if (from && from.path === path) {
		history.goBack();
	} else {
		history.push(path);
	}
}
